/**
 * Tipos canônicos do domínio Conversa + entidades correlatas.
 *
 * Fonte da verdade dos enums:
 *   - ConversationStatus (9 valores): `Conversation.yaml` schema do OpenAPI v1
 *     (D.2-api-02b — pós-ADR-079). NOTA: ADR-054 (Lifecycle) propõe um
 *     conjunto de 7 estados mais consolidado (open, awaiting_customer,
 *     awaiting_attendant, paused, resolved, closed_unresolved, archived).
 *     A unificação fica para Fatia F. Por agora seguimos Conversation.yaml.
 *   - ChannelKind / OperationModeKind / SentimentTrend: idem schema OpenAPI.
 *   - IntentState (ADR-108) / MetaSessionType (ADR-127) /
 *     AppointmentStatus (ADR-115): expostos como union types para o frontend
 *     consumir badges nas fixtures, mesmo sem schema Prisma desses domínios
 *     ainda (Fatia futura).
 */
export const CONVERSATION_STATUSES = [
    'new',
    'assigned',
    'in-progress',
    'awaiting-customer',
    'awaiting-internal-action',
    'awaiting-temporal',
    'paused',
    'closed',
    'reopened',
    // Fatia F (ADR-054) — estados canônicos do lifecycle. Coexistem com os legacy
    // acima no enum Prisma; a serialização (kebab↔snake) precisa mapear TODOS,
    // senão `GET /v1/conversations` lança ao serializar uma conversa encerrada.
    'awaiting-attendant',
    'resolved',
    'closed-unresolved',
    'archived',
];
export const CHANNEL_KINDS = [
    'whatsapp-cloud-api',
    'webchat',
    'email',
    'telegram',
    'instagram-dm',
    /**
     * Simulador de conversa — ferramenta INTERNA e do tenant (spec
     * `2026-08-18-webchat-do-cliente-tres-cenarios` §4). NÃO é canal de cliente: existe para
     * travar a origem e exercitar o motor sem canal real. Separado de `webchat`, que fica
     * reservado ao futuro cliente de produção, para que dado de laboratório e dado de cliente
     * nunca compartilhem rótulo em linha auditável (Princípio 17).
     */
    'simulador',
];
/**
 * Modos operacionais canonical (Caderno Parte II §22 + ADR-029):
 *   - H_a: humano dirige (default)
 *   - H_b: híbrido (IA propõe, atendente aprova)
 *   - H_c: IA dirige autonomamente (com gating de proteção)
 *   - H_d: delegação manual canonical (atendente delegou explicitamente)
 *
 * H_d adicionado em Task 59 (drift canonical resolvido — `mode-badge--h-d`
 * + Caderno + ConversationFilters já tratavam H_d, mas shared canonical
 * estava restrito a 3 modos).
 */
export const OPERATION_MODE_KINDS = ['H_a', 'H_b', 'H_c', 'H_d'];
export const SENTIMENT_TRENDS = ['ascending', 'stable', 'descending'];
/**
 * Intent.state — ADR-108 (7 estados de lifecycle). Não confundir com o
 * enum de 4 estados que aparece no `fixtures.js` do Brief 2B
 * (`tentative/confirmed/revised/resolved`); aquele é apenas referência
 * visual, este é o canônico para consumo do Engine.
 *
 * Mapping legado → canônico:
 *   tentative → detected
 *   revised   → evolving
 *   resolved  → completed
 */
export const INTENT_STATES = [
    'detected',
    'confirmed',
    'evolving',
    'branched',
    'completed',
    'abandoned',
    'misclassified',
];
/**
 * MetaSession.type — ADR-127. 5 categorias Meta + ctwa (origem
 * Click to WhatsApp Ad).
 *
 * `expired` NÃO é tipo — é estado derivado de `customer_care` quando
 * a janela de 24h da Meta fechou. Use `isExpired: boolean` em modelos
 * separadamente.
 */
export const META_SESSION_TYPES = [
    'customer_care',
    'service',
    'utility',
    'authentication',
    'marketing',
    'ctwa',
];
/** Appointment.status — ADR-115 + Sprint Backend Operacional Brief 2A r7b (Agent Y).
 *
 * ADR-115 canonical declara 6 estados. Sprint Backend Operacional adiciona
 * `imminent` como UX-derived (alias de "confirmed + scheduledAt em <24h")
 * para casamento canônico com fixture (Agent C Sprint Visual r7b).
 *
 * Cron de transição scheduled→imminent + imminent→no_show pode ser adicionado
 * em PR-B futuro. v1 permite `imminent` como input direto via PATCH.
 *
 * `pending` do Brief 2B fixtures NÃO é canônico; mapeia para `scheduled`.
 */
export const APPOINTMENT_STATUSES = [
    'scheduled',
    'confirmed',
    'imminent',
    'completed',
    'cancelled',
    'no_show',
    'rescheduled',
];
/** Sprint Backend Operacional Brief 2A r7b — Appointment.type (Agent Y). */
export const APPOINTMENT_TYPES = [
    'technical_visit',
    'in_person_meeting',
    'video_call',
    'phone_call',
    'delivery',
    'other',
];
/** Sprint Backend Operacional Brief 2A r7b — Appointment.source (Agent Y). */
export const APPOINTMENT_SOURCES = ['ai', 'attendant', 'customer'];
/** Estados terminais — não podem transicionar para outros. */
export const APPOINTMENT_TERMINAL_STATUSES = [
    'completed',
    'cancelled',
    'no_show',
    'rescheduled',
];
export * from './wire';
export * from './group-by-person';
export * from './terminal-status';
export * from './window-24h';
//# sourceMappingURL=index.js.map